import React, { useEffect, useState, useCallback } from "react";
import { api } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { PaperPlaneTilt, FilePdf, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

const money = (v, cur) => `${cur === "USD" ? "US$" : "RD$"} ${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const TransfersAdminPanel = () => {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [currency, setCurrency] = useState("all");

  const params = useCallback(() => {
    const p = {};
    if (q.trim()) p.q = q.trim();
    if (start) p.start = start;
    if (end) p.end = end;
    if (currency !== "all") p.currency = currency;
    return p;
  }, [q, start, end, currency]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/transfers", { params: params() })
      .then((r) => setRows(r.data)).catch(() => setRows([])).finally(() => setLoading(false));
  }, [params]);

  // debounce search/filters
  useEffect(() => {
    const id = setTimeout(load, 350);
    return () => clearTimeout(id);
  }, [load]);

  const downloadReceipt = async (tid, ref) => {
    try {
      const res = await api.get(`/admin/transfers/${tid}/receipt`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `recibo_transferencia_${ref || tid}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(t("error")); }
  };

  return (
    <div className="space-y-6" data-testid="admin-transfers-panel">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-lg bg-emerald-100 flex items-center justify-center"><PaperPlaneTilt size={24} weight="duotone" className="text-emerald-800" /></div>
        <div>
          <h2 className="font-display font-bold text-emerald-900 text-xl">{t("all_transfers_title")}</h2>
          <p className="text-sm text-slate-500">{t("all_transfers_sub")}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
          <Label className="text-xs text-slate-500">{t("search")}</Label>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="transfers-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("transfers_search_ph")} className="h-10 pl-9" />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-slate-500">{t("from_date")}</Label>
          <Input data-testid="transfers-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-10 w-[160px]" />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-slate-500">{t("to_date")}</Label>
          <Input data-testid="transfers-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-10 w-[160px]" />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-slate-500">{t("currency")}</Label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger data-testid="transfers-currency" className="h-10 w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_currencies")}</SelectItem>
              <SelectItem value="DOP">RD$ (DOP)</SelectItem>
              <SelectItem value="USD">US$ (USD)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {(q || start || end || currency !== "all") && (
          <Button data-testid="transfers-clear" variant="outline" onClick={() => { setQ(""); setStart(""); setEnd(""); setCurrency("all"); }} className="h-10">{t("clear_filters")}</Button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-x-auto" data-testid="transfers-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-400 border-b border-gray-100">
              <th className="px-4 py-2.5 font-medium">{t("date")}</th>
              <th className="px-4 py-2.5 font-medium">{t("reference")}</th>
              <th className="px-4 py-2.5 font-medium">{t("from_account")}</th>
              <th className="px-4 py-2.5 font-medium">{t("to_account")}</th>
              <th className="px-4 py-2.5 font-medium text-right">{t("mv_amount")}</th>
              <th className="px-4 py-2.5 font-medium text-right">{t("receipt")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">{t("loading")}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400" data-testid="transfers-empty">{t("no_transfers")}</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-gray-50 last:border-0" data-testid={`admin-transfer-row-${r.id}`}>
                <td className="px-4 py-2.5 text-slate-600">{(r.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-2.5 font-mono-num text-slate-500">{r.reference}</td>
                <td className="px-4 py-2.5"><span className="text-emerald-900 font-semibold">{r.from_name}</span><span className="text-xs text-slate-400 ml-1 font-mono-num">{r.from_account_number}</span></td>
                <td className="px-4 py-2.5"><span className="text-emerald-900 font-semibold">{r.to_name}</span><span className="text-xs text-slate-400 ml-1 font-mono-num">{r.to_account_number}</span></td>
                <td className="px-4 py-2.5 text-right font-mono-num font-bold text-emerald-800">{money(r.amount, r.currency)}</td>
                <td className="px-4 py-2.5 text-right">
                  <Button data-testid={`admin-transfer-receipt-${r.id}`} size="sm" variant="outline" onClick={() => downloadReceipt(r.id, r.reference)} className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1.5"><FilePdf size={14} weight="bold" /> {t("receipt")}</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
